/** Блок «Иерархия» вкладки «Дерево» — разрезы лесенкой, как группировки в настройках отчёта 1С.
 *
 *  Свёрнут (по умолчанию) — одна строка с цепочкой разрезов. Развёрнут — готовые виды, панель
 *  «Добавить разрез / ↑ / ↓ / Удалить» (действует на выделенную строку), таблица разрезов
 *  с выбором измерения, «глазиком» (временно выключить разрез, не удаляя) и перетаскиванием за
 *  ручку. Каждое изменение сразу уходит в адрес (`onLevels`) — дерево перестраивается.
 *  Свёрнутость и выделение — состояние компонента, в адрес не пишутся. */
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import React, { useEffect, useId, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Seg } from "./shared";
import {
  activeLevels,
  addLevel,
  canRemoveLevel,
  MAX_LEVELS,
  moveLevel,
  removeLevel,
  setLevel,
  toggleLevelOff,
  type Hierarchy,
} from "./state";
import type { TreePreset } from "./mp";

type Dim = { code: string; label: string };

function levelsText(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} уровень`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} уровня`;
  return `${n} уровней`;
}

function countText(h: Hierarchy): string {
  const active = activeLevels(h).length;
  const off = h.levels.length - active;
  return off > 0 ? `${levelsText(active)}, выключено ${off}` : levelsText(active);
}

export function HierarchyPanel({
  hierarchy,
  dims,
  presets,
  treePreset,
  onLevels,
  onPreset,
}: {
  hierarchy: Hierarchy;
  /** измерения из /meta (код → название); null — мета ещё не пришла */
  dims: Dim[] | null;
  /** готовые виды площадки (mp.ts) */
  presets: TreePreset[];
  treePreset: string | null;
  onLevels: (h: Hierarchy) => void;
  onPreset: (id: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  // свёрнутая и развёрнутая шапки — разные <button>: после переключения фокус переводим на новую
  const toggleRef = useRef<HTMLButtonElement>(null);
  const toggledByUser = useRef(false);
  useEffect(() => {
    if (!toggledByUser.current) return;
    toggledByUser.current = false;
    toggleRef.current?.focus();
  }, [open]);
  const toggle = (next: boolean) => {
    toggledByUser.current = true;
    setOpen(next);
  };
  const dimLabel = (code: string): string =>
    dims?.find((d) => d.code === code)?.label ?? code;

  if (!open) {
    return (
      <button
        type="button"
        ref={toggleRef}
        aria-expanded={false}
        onClick={() => toggle(true)}
        className="flex w-full items-center gap-2 rounded-lg border bg-card min-h-8 px-3 py-1 text-left text-xs text-card-foreground shadow-sm hover:bg-accent/50"
      >
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        <span className="font-medium">Иерархия</span>
        <span
          className="flex min-w-0 flex-wrap items-center gap-x-1.5"
          data-testid="hierarchy-chain"
        >
          {hierarchy.levels.map((code, i) => (
            <React.Fragment key={`${i}-${code}`}>
              {i > 0 && <span className="text-muted-foreground">›</span>}
              {hierarchy.off.includes(code) ? (
                <s
                  className="text-muted-foreground"
                  title="Разрез временно выключен"
                >
                  {dimLabel(code)}
                </s>
              ) : (
                <span>{dimLabel(code)}</span>
              )}
            </React.Fragment>
          ))}
        </span>
        <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          {countText(hierarchy)} · изменить
        </span>
      </button>
    );
  }

  return (
    <section
      aria-label="Иерархия"
      className="rounded-lg border bg-card text-card-foreground shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
        <button
          type="button"
          ref={toggleRef}
          aria-expanded
          onClick={() => toggle(false)}
          className="inline-flex items-center gap-1.5 text-xs font-medium"
        >
          <ChevronDown className="size-4 text-muted-foreground" />
          Иерархия
        </button>
        <span className="text-xs text-muted-foreground">
          {countText(hierarchy)}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Готовые виды:</span>
          <Seg
            label="Готовые виды дерева"
            value={treePreset}
            options={presets.map((p) => ({ id: p.id, label: p.label }))}
            onChange={onPreset}
          />
        </div>
      </div>
      <HierarchyEditor
        hierarchy={hierarchy}
        dims={dims}
        dimLabel={dimLabel}
        onLevels={onLevels}
      />
    </section>
  );
}

function HierarchyEditor({
  hierarchy: h,
  dims,
  dimLabel,
  onLevels,
}: {
  hierarchy: Hierarchy;
  dims: Dim[] | null;
  dimLabel: (code: string) => string;
  onLevels: (h: Hierarchy) => void;
}): React.ReactElement {
  const n = h.levels.length;
  const uid = useId();
  const rowId = (i: number) => `${uid}-row-${i}`;
  const gridRef = useRef<React.ElementRef<"table">>(null);
  // выделение — по позиции; после перестановок пересчитывается в обработчиках
  const [selRaw, setSel] = useState<number | null>(null);
  const sel = selRaw !== null && selRaw < n ? selRaw : null;
  // перетаскивание: откуда и перед какой строкой вставить (n — в конец)
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const dimCodes = dims?.map((d) => d.code) ?? [];
  const canAdd =
    dims !== null && n < MAX_LEVELS && dimCodes.some((d) => !h.levels.includes(d));
  const canUp = sel !== null && sel > 0;
  const canDown = sel !== null && sel < n - 1;
  const canRemove = sel !== null && canRemoveLevel(h, sel);
  const activeCount = activeLevels(h).length;

  const add = () => {
    if (!canAdd) return;
    onLevels(addLevel(h, dimCodes, sel ?? undefined));
    setSel(sel !== null ? sel + 1 : n);
  };
  const move = (dir: -1 | 1) => {
    if (sel === null || (dir === -1 ? !canUp : !canDown)) return;
    onLevels(moveLevel(h, sel, sel + dir));
    setSel(sel + dir);
    // кнопка упёрлась в край и станет disabled — фокус не должен уйти на body
    if (sel + dir === (dir === -1 ? 0 : n - 1)) gridRef.current?.focus();
  };
  const remove = () => {
    if (sel === null || !canRemove) return;
    const next = removeLevel(h, sel);
    const nextSel = Math.min(sel, n - 2);
    onLevels(next);
    setSel(nextSel);
    if (!canRemoveLevel(next, nextSel)) gridRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // стрелки внутри select/кнопок — их собственные
    if (e.target !== e.currentTarget) return;
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const dir = e.key === "ArrowUp" ? -1 : 1;
      if (e.altKey) move(dir);
      else if (sel === null) setSel(dir === -1 ? n - 1 : 0);
      else setSel(Math.min(n - 1, Math.max(0, sel + dir)));
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      remove();
    }
  };

  const endDrag = () => {
    setDragFrom(null);
    setDropAt(null);
  };
  const drop = () => {
    if (dragFrom !== null && dropAt !== null) {
      const to = dropAt > dragFrom ? dropAt - 1 : dropAt;
      if (to !== dragFrom) {
        onLevels(moveLevel(h, dragFrom, to));
        setSel(to);
      }
    }
    endDrag();
  };
  // линия-индикатор — только там, где перенос что-то меняет
  const showDrop =
    dragFrom !== null &&
    dropAt !== null &&
    dropAt !== dragFrom &&
    dropAt !== dragFrom + 1;

  // лесенка по активным строкам: отступ строки = число активных разрезов выше неё
  let activeAbove = 0;
  const depths = h.levels.map((code) => {
    const d = activeAbove;
    if (!h.off.includes(code)) activeAbove += 1;
    return d;
  });

  const toolBtn = "h-8 gap-1.5 px-2.5 text-xs";

  return (
    <div className="space-y-2 p-3">
      <div
        role="toolbar"
        aria-label="Действия с разрезами"
        className="flex flex-wrap items-center gap-1.5"
      >
        <Button
          variant="outline"
          size="sm"
          className={toolBtn}
          disabled={!canAdd}
          onClick={add}
          title={
            n >= MAX_LEVELS
              ? `Не больше ${MAX_LEVELS} разрезов`
              : "Добавить первый свободный разрез после выделенной строки"
          }
        >
          <Plus /> Добавить разрез
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={!canUp}
          onClick={() => move(-1)}
          title="Переместить вверх (Alt+↑)"
          aria-label="Переместить вверх"
        >
          <ArrowUp />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={!canDown}
          onClick={() => move(1)}
          title="Переместить вниз (Alt+↓)"
          aria-label="Переместить вниз"
        >
          <ArrowDown />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className={toolBtn}
          disabled={!canRemove}
          onClick={remove}
          title="Удалить выделенный разрез (Delete/⌫)"
        >
          <Trash2 /> Удалить
        </Button>
      </div>

      <div aria-live="polite" className="sr-only">
        {sel !== null && `Выделен разрез ${sel + 1}: ${dimLabel(h.levels[sel]!)}`}
      </div>
      <table
        ref={gridRef}
        role="grid"
        tabIndex={0}
        aria-activedescendant={sel !== null ? rowId(sel) : undefined}
        aria-label="Разрезы иерархии"
        onKeyDown={onKeyDown}
        onDragOver={(e) => {
          if (dragFrom !== null) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          drop();
        }}
        className="w-full max-w-xl border-separate border-spacing-0 rounded-md border text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <thead>
          <tr>
            <th className="w-7 border-b" aria-label="Перетащить" />
            <th className="w-8 border-b px-1 py-1.5 text-right text-[11px] font-medium text-muted-foreground">
              №
            </th>
            <th className="border-b px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Разрез
            </th>
            <th className="w-10 border-b" aria-label="Включён" />
          </tr>
        </thead>
        <tbody>
          {h.levels.map((code, i) => {
            const isOff = h.off.includes(code);
            const depth = depths[i]!;
            const lastActive = !isOff && activeCount <= 1;
            return (
              <tr
                key={i}
                id={rowId(i)}
                aria-selected={sel === i}
                data-testid="hierarchy-row"
                data-off={isOff || undefined}
                onClick={() => setSel(i)}
                onDragOver={(e) => {
                  if (dragFrom === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  const r = e.currentTarget.getBoundingClientRect();
                  setDropAt(e.clientY > r.top + r.height / 2 ? i + 1 : i);
                }}
                className={cn(
                  sel === i
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted/50",
                  showDrop &&
                    dropAt === i &&
                    "shadow-[inset_0_2px_0_0_hsl(var(--primary))]",
                  showDrop &&
                    dropAt === n &&
                    i === n - 1 &&
                    "shadow-[inset_0_-2px_0_0_hsl(var(--primary))]",
                )}
              >
                <td className="px-1 py-1 text-muted-foreground">
                  <span
                    draggable
                    data-testid="hierarchy-grip"
                    title="Перетащите, чтобы поменять порядок"
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(i));
                      const row = e.currentTarget.closest("tr");
                      if (row) e.dataTransfer.setDragImage(row, 16, 16);
                      setDragFrom(i);
                      setSel(i);
                    }}
                    onDragEnd={endDrag}
                    className="inline-flex cursor-grab active:cursor-grabbing"
                  >
                    <GripVertical className="size-4" />
                  </span>
                </td>
                <td className="px-1 py-1 text-right tabular-nums text-muted-foreground">
                  {i + 1}
                </td>
                <td className="px-2 py-1">
                  <div
                    className={cn(
                      "flex items-center gap-1.5",
                      isOff && "text-muted-foreground opacity-60",
                    )}
                    style={{ paddingLeft: `${depth * 1.25}rem` }}
                  >
                    {depth > 0 && (
                      <span aria-hidden className="text-muted-foreground">
                        └
                      </span>
                    )}
                    <select
                      aria-label={`Разрез ${i + 1}`}
                      className={cn(
                        "h-8 rounded-md border bg-background px-1.5 text-xs",
                        isOff && "line-through",
                      )}
                      value={code}
                      onChange={(e) => onLevels(setLevel(h, i, e.target.value))}
                    >
                      {!dims?.some((x) => x.code === code) && (
                        <option value={code}>{code}</option>
                      )}
                      {dims?.map((x) => (
                        <option key={x.code} value={x.code}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td className="px-1 py-1 text-center">
                  <button
                    type="button"
                    aria-label={`${isOff ? "Включить" : "Временно выключить"} разрез «${dimLabel(code)}»`}
                    title={
                      isOff
                        ? "Включить"
                        : lastActive
                          ? "Последний активный разрез выключить нельзя"
                          : "Временно выключить из иерархии"
                    }
                    disabled={lastActive}
                    onClick={() => onLevels(toggleLevelOff(h, code))}
                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-30"
                  >
                    {isOff ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground">
        Клик по строке — выделить; ↑/↓ — выделение, Alt+↑/↓ — перенос, Delete/⌫ —
        удалить; порядок можно менять перетаскиванием за ручку. Глазик временно
        выключает разрез: он остаётся в списке, но в дерево не идёт.
      </p>
    </div>
  );
}
